import { useMemo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { findAnchorTarget } from '../../_shared/lib/findAnchorTarget';

interface TocItem {
  id: string;
  text: string;
  level: 1 | 2 | 3;
}

interface StandardsTocProps {
  markdownText: string;
}

function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^\w\s가-힣-]/g, '')
    .replace(/[\s_]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function extractHeadings(markdown: string): TocItem[] {
  const lines = markdown.split('\n');
  const items: TocItem[] = [];
  const seen = new Map<string, number>();

  for (const line of lines) {
    const m = line.match(/^(#{1,3})\s+(.+)$/);
    if (!m) continue;
    const level = m[1].length as 1 | 2 | 3;
    const rawText = m[2].replace(/\*\*|__|`/g, '').trim();
    let id = slugify(rawText);
    const count = seen.get(id) ?? 0;
    seen.set(id, count + 1);
    if (count > 0) id = `${id}-${count}`;
    items.push({ id, text: rawText, level });
  }
  return items;
}

export function StandardsToc({ markdownText }: StandardsTocProps) {
  const { t } = useTranslation();
  const items = useMemo(() => extractHeadings(markdownText), [markdownText]);
  const [activeId, setActiveId] = useState<string>('');

  useEffect(() => {
    const handleScroll = () => {
      const headings = document.querySelectorAll<HTMLElement>(
        '[data-standards-content] h1[id], [data-standards-content] h2[id], [data-standards-content] h3[id]'
      );
      let current = '';
      for (const el of headings) {
        if (el.getBoundingClientRect().top <= 120) {
          current = el.id;
        }
      }
      setActiveId(current);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollTo = (id: string) => {
    // #696 — MarkdownView sanitize clobberPrefix(user-content-) 대응 SSOT 조회
    const el = findAnchorTarget(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  if (items.length === 0) return null;

  return (
    <nav
      aria-label={t('devStandardsViewer.toc.label')}
      style={{
        position: 'sticky',
        top: 80,
        maxHeight: 'calc(100vh - 120px)',
        overflowY: 'auto',
        padding: 'var(--spacing-4)',
        borderRight: '1px solid var(--color-surface-border)',
        minWidth: 200,
        maxWidth: 260,
        flexShrink: 0,
      }}
    >
      <div
        style={{
          fontSize: 'var(--font-size-xs)',
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.08em',
          color: 'var(--color-text-muted)',
          marginBottom: 'var(--spacing-3)',
        }}
      >
        {t('devStandardsViewer.toc.label')}
      </div>
      <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
        {items.map((item) => (
          <li key={item.id} style={{ margin: 0 }}>
            <button
              type="button"
              onClick={() => scrollTo(item.id)}
              aria-current={activeId === item.id ? 'location' : undefined}
              style={{
                display: 'block',
                width: '100%',
                textAlign: 'left',
                background: activeId === item.id ? 'var(--color-surface-muted)' : 'transparent',
                border: 'none',
                borderLeft: activeId === item.id
                  ? '2px solid var(--color-brand-primary)'
                  : '2px solid transparent',
                paddingLeft:
                  item.level === 1
                    ? 'var(--spacing-2)'
                    : item.level === 2
                    ? 'var(--spacing-4)'
                    : 'var(--spacing-6)',
                paddingTop: 'var(--spacing-1)',
                paddingBottom: 'var(--spacing-1)',
                paddingRight: 'var(--spacing-2)',
                cursor: 'pointer',
                fontSize:
                  item.level === 1
                    ? 'var(--font-size-sm)'
                    : 'var(--font-size-xs)',
                fontWeight: item.level === 1 ? 600 : 400,
                color:
                  activeId === item.id
                    ? 'var(--color-brand-primary)'
                    : 'var(--color-text-secondary)',
                fontFamily: 'var(--font-family-sans)',
                lineHeight: 1.5,
                transition: 'color 0.15s, border-color 0.15s',
              }}
            >
              {item.text}
            </button>
          </li>
        ))}
      </ul>
    </nav>
  );
}
